import type { IframePoolItem } from '../../../modules/api';
import { formatPrice } from '../../../modules/currency';
import { Close, Image, Modal, SheetFrame } from '../../../modules/design-system';
import { useIsDesktopViewport } from '../shared/useIsDesktopViewport';

type Props = {
  item: IframePoolItem;
  onClose: () => void;
};

const TITLE_ID = 'glass-hit-sheet-title';
const SHEET_TITLE = 'Potential Hit';

/** Desktop dialog and phone sheet (repackz-handoff §8.3 `HitSheet`). */
const VARIANTS = {
  desktop: {
    slab: 'h-[320px]',
    name: 'text-[18px] leading-[24px] font-extrabold uppercase',
    value: 'text-[32px] leading-[40px] font-bold',
    back: 'h-[52px] text-[15px]',
    slabWidth: 216,
    slabHeight: 320,
  },
  phone: {
    slab: 'h-[302px]',
    name: 'text-[15px] leading-[22px] font-bold',
    value: 'text-[36px] leading-[45px] font-extrabold',
    back: 'h-11 text-[15px]',
    slabWidth: 204,
    slabHeight: 302,
  },
} as const;

/**
 * A potential hit opened from the storefront: the slab, its name and its
 * Fair Market Value, then BACK. A centred glass dialog 440 wide on the
 * desktop, a glass sheet on a phone.
 */
export function HitSheet({ item, onClose }: Props) {
  const isDesktop = useIsDesktopViewport();

  if (isDesktop) {
    return (
      <Modal onClose={onClose} inspectName="HitSheet" ariaLabelledBy={TITLE_ID} widthClassName="max-w-[440px]">
        <div className="rounded-[32px] border-[0.5px] border-solid p-5 [border-color:var(--glass-panel-rim)] [background:var(--glass-sheet-fill)] [backdrop-filter:blur(var(--glass-panel-blur))]">
          <HitDetails item={item} variant="desktop" onClose={onClose} />
        </div>
      </Modal>
    );
  }

  return (
    <SheetFrame
      onClose={onClose}
      ariaLabelledBy={TITLE_ID}
      inspectName="HitSheet"
      panelClassName={
        '!inset-x-[6px] !bottom-[calc(6px+var(--page-safe-bottom,0px))] !px-4 rounded-[32px] border-[0.5px] border-solid ' +
        '[border-color:var(--glass-panel-rim)] [background:var(--glass-sheet-fill)] [backdrop-filter:blur(var(--glass-panel-blur))]'
      }
      handleClassName="[background:var(--glass-sheet-handle)]"
    >
      <HitDetails item={item} variant="phone" onClose={onClose} />
    </SheetFrame>
  );
}

type DetailsProps = {
  item: IframePoolItem;
  variant: keyof typeof VARIANTS;
  onClose: () => void;
};

function HitDetails({ item, variant, onClose }: DetailsProps) {
  const v = VARIANTS[variant];
  return (
    <div className="flex flex-col items-center gap-4 text-center text-white">
      <header className="relative flex h-9 w-full items-center justify-center">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute left-0 flex size-9 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-solid [border-color:var(--glass-panel-rim)] [background:var(--glass-close-fill)]"
        >
          <Close size={12} />
        </button>
        <h2 id={TITLE_ID} className="text-[17px] font-semibold leading-[22px]">
          {SHEET_TITLE}
        </h2>
      </header>
      <Image
        src={item.imageUrl}
        alt={item.title}
        className={`relative w-full ${v.slab}`}
        imgClassName="absolute inset-0 h-full w-full object-contain"
        width={v.slabWidth}
        height={v.slabHeight}
        quality={95}
      />
      <p className={`m-0 ${v.name}`}>{item.title}</p>
      {item.itemPrice > 0 && (
        <div className="flex flex-col items-center">
          <span className="text-[13px] leading-[18px] [color:var(--glass-text-muted)]">Fair Market Value</span>
          <span className={v.value}>{formatPrice(item.itemPrice, 2)}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onClose}
        className={`${v.back} w-full cursor-pointer rounded-full border border-solid font-extrabold uppercase tracking-[-0.3px] [border-color:var(--glass-rip-rim)] transition-[filter] hover:brightness-125`}
      >
        Back
      </button>
    </div>
  );
}
